import React, { useState, useEffect } from 'react';
import {
  Shield,
  Save,
  RotateCcw,
  Users,
  Building,
  Calculator,
  Database,
  Eye,
  FileText,
  Package,
  Loader2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { api } from '../../services/api';

// ─── Which permissions are enforced per module ───────────────────────────────
// Modules with full CRUD: field_instructions, users, roles_departments
// Modules with READ + UPDATE: authorization, profile_settings
// All others (categories, subcategories, settings parent): READ only
type PermKey = 'read' | 'create' | 'update' | 'delete';
const ALL_PERMS: PermKey[] = ['read', 'create', 'update', 'delete'];

const MODULE_PERMS: Record<string, PermKey[]> = {
  field_instructions: ['read', 'create', 'update', 'delete'],
  users:              ['read', 'create', 'update', 'delete'],
  roles_departments:  ['read', 'create', 'update', 'delete'],
  authorization:      ['read', 'update'],
  profile_settings:   ['read', 'update'],
};

// Any module not listed above defaults to READ only
const getApplicablePerms = (moduleId: string): PermKey[] =>
  MODULE_PERMS[moduleId] ?? ['read'];

interface Permission {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

interface Module {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  permissions: Permission;
  parentModuleId: string | null;
}

interface Role {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string;
  email: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getModuleIcon = (moduleId: string) => {
  if (moduleId.includes('business_partner') || moduleId.includes('partner')) return Users;
  if (moduleId.includes('item')            || moduleId.includes('product'))  return Package;
  if (moduleId.includes('financial')       || moduleId.includes('account'))  return Calculator;
  if (moduleId.includes('field_instruct'))                                    return FileText;
  if (moduleId.includes('user'))                                              return Users;
  if (moduleId.includes('roles')           || moduleId.includes('department'))return Building;
  if (moduleId.includes('auth'))                                              return Shield;
  return Database;
};

const emptyPerm = (): Permission => ({ read: false, create: false, update: false, delete: false });

const PERM_LABELS: Record<PermKey, { label: string; sub: string }> = {
  read:   { label: 'READ',   sub: 'View & access'   },
  create: { label: 'CREATE', sub: 'Add new items'   },
  update: { label: 'UPDATE', sub: 'Edit existing'   },
  delete: { label: 'DELETE', sub: 'Remove items'    },
};

// ─── Component ────────────────────────────────────────────────────────────────

const AuthorizationManagement: React.FC = () => {
  const [selectedRole, setSelectedRole]             = useState('');
  const [selectedUser, setSelectedUser]             = useState('');
  const [modules, setModules]                       = useState<Module[]>([]);
  const [roles, setRoles]                           = useState<Role[]>([]);
  const [users, setUsers]                           = useState<User[]>([]);
  const [loading, setLoading]                       = useState(false);
  const [loadingUsers, setLoadingUsers]             = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [saving, setSaving]                         = useState(false);
  const [hasChanges, setHasChanges]                 = useState(false);
  const [saveMessage, setSaveMessage]               = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expandedGroups, setExpandedGroups]         = useState<Set<string>>(new Set());

  useEffect(() => { loadRoles(); loadModules(); }, []);

  useEffect(() => {
    if (selectedRole) loadUsers(selectedRole);
    else { setUsers([]); setSelectedUser(''); }
  }, [selectedRole]);

  useEffect(() => {
    if (selectedUser && modules.length > 0) loadUserPermissions(selectedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  // ── Data loaders ────────────────────────────────────────────────────────────

  const loadRoles = async () => {
    try {
      const res = await api.get('/roles');
      if (res.success) setRoles(res.data.map((r: any) => ({ id: r.role_id, name: r.role_name })));
    } catch (e) { console.error('Error loading roles:', e); }
  };

  const loadUsers = async (roleId: string) => {
    try {
      setLoadingUsers(true);
      setSelectedUser('');
      const res = await api.get(`/users?role=${roleId}`);
      setUsers(res.success && res.data?.users
        ? res.data.users.map((u: any) => ({ id: u.user_id, name: u.user_name, email: u.user_email }))
        : []
      );
    } catch (e) { console.error('Error loading users:', e); setUsers([]); }
    finally { setLoadingUsers(false); }
  };

  const loadModules = async () => {
    try {
      setLoading(true);
      const res = await api.get('/modules');
      if (res.success) {
        const seenId = new Set<string>();
        setModules(
          res.data
            .filter((m: any) => {
              if (seenId.has(m.module_id)) return false;
              seenId.add(m.module_id);
              return true;
            })
            .map((m: any) => ({
              id:             m.module_id,
              name:           m.module_name,
              icon:           getModuleIcon(m.module_id),
              permissions:    emptyPerm(),
              parentModuleId: m.parent_module_id || null,
            }))
        );
      }
    } catch (e) { console.error('Error loading modules:', e); }
    finally { setLoading(false); }
  };

  const loadUserPermissions = async (userId: string) => {
    try {
      setLoadingPermissions(true);
      setHasChanges(false);
      const res = await api.get(`/users/${userId}/permissions`);
      if (res.success && res.data) {
        setModules(prev => prev.map(mod => {
          const p = res.data.find((lp: any) =>
            lp.module_id === mod.id || lp.module_name === mod.name
          );
          return {
            ...mod,
            permissions: p
              ? { read: !!p.can_read, create: !!p.can_create, update: !!p.can_update, delete: !!p.can_delete }
              : emptyPerm(),
          };
        }));
      }
    } catch (e) { console.error('Error loading permissions:', e); }
    finally { setLoadingPermissions(false); }
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const toggleGroup = (parentId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const handlePermissionChange = (moduleId: string, perm: PermKey | 'all', value: boolean) => {
    setHasChanges(true);
    setSaveMessage(null);
    setModules(prev => prev.map(mod => {
      if (mod.id !== moduleId) return mod;
      if (perm === 'all') {
        const applicable = getApplicablePerms(mod.id);
        const updated = { ...mod.permissions };
        applicable.forEach(p => { updated[p] = value; });
        return { ...mod, permissions: updated };
      }
      return { ...mod, permissions: { ...mod.permissions, [perm]: value } };
    }));
  };

  // Toggle all children of a parent module
  const handleParentToggle = (parentId: string, value: boolean) => {
    setHasChanges(true);
    setSaveMessage(null);
    setModules(prev => prev.map(mod => {
      if (mod.parentModuleId !== parentId) return mod;
      const applicable = getApplicablePerms(mod.id);
      const updated = { ...mod.permissions };
      applicable.forEach(p => { updated[p] = value; });
      return { ...mod, permissions: updated };
    }));
  };

  const handleQuickAction = (perm: PermKey | 'all', value: boolean) => {
    setHasChanges(true);
    setSaveMessage(null);
    setModules(prev => prev.map(mod => {
      const applicable = getApplicablePerms(mod.id);
      if (perm === 'all') {
        const updated = { ...mod.permissions };
        applicable.forEach(p => { updated[p] = value; });
        return { ...mod, permissions: updated };
      }
      if (!applicable.includes(perm)) return mod;
      return { ...mod, permissions: { ...mod.permissions, [perm]: value } };
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) { setSaveMessage({ type: 'error', text: 'Please select a user first.' }); return; }
    try {
      setSaving(true);
      setSaveMessage(null);
      const permissions = modules.map(m => ({
        module_id:  m.id,
        can_read:   m.permissions.read,
        can_create: m.permissions.create,
        can_update: m.permissions.update,
        can_delete: m.permissions.delete,
      }));
      await api.put(`/users/${selectedUser}/permissions`, { permissions });
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Permissions saved successfully.' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (e: any) {
      setSaveMessage({ type: 'error', text: e?.message || 'Failed to save permissions.' });
    } finally { setSaving(false); }
  };

  // ── Derived state ────────────────────────────────────────────────────────────

  const canEdit          = !loadingPermissions && !!selectedUser;
  const selectedUserData = users.find(u => u.id === selectedUser);

  // Separate parent modules (no parent) from child modules (has parent)
  const parentModules = modules.filter(m => !m.parentModuleId);
  const childModules  = modules.filter(m => !!m.parentModuleId);

  // Group children by parent
  const childrenByParent: Record<string, Module[]> = {};
  childModules.forEach(m => {
    if (!m.parentModuleId) return;
    if (!childrenByParent[m.parentModuleId]) childrenByParent[m.parentModuleId] = [];
    childrenByParent[m.parentModuleId].push(m);
  });

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderModuleRow = (mod: Module, isChild = false) => {
    const Icon       = mod.icon;
    const applicable = getApplicablePerms(mod.id);
    const hasAll     = applicable.length > 1;
    const allChecked = applicable.every(p => mod.permissions[p]);

    return (
      <tr key={mod.id} className={`hover:bg-gray-50 transition-colors ${isChild ? 'bg-gray-50/50' : ''}`}>
        <td className="px-6 py-3 whitespace-nowrap">
          <div className={`flex items-center gap-3 ${isChild ? 'pl-8' : ''}`}>
            <Icon className={`w-4 h-4 flex-shrink-0 ${isChild ? 'text-gray-400' : 'text-gray-500'}`} />
            <span className={`text-sm ${isChild ? 'text-gray-600' : 'font-medium text-gray-900'}`}>{mod.name}</span>
          </div>
        </td>

        {ALL_PERMS.map(perm => (
          <td key={perm} className="px-6 py-3 whitespace-nowrap text-center">
            {applicable.includes(perm) ? (
              <input
                type="checkbox"
                checked={mod.permissions[perm]}
                onChange={e => handlePermissionChange(mod.id, perm, e.target.checked)}
                disabled={!canEdit}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              />
            ) : (
              <span className="text-gray-300 text-lg select-none">—</span>
            )}
          </td>
        ))}

        <td className="px-6 py-3 whitespace-nowrap text-center">
          {hasAll ? (
            <input
              type="checkbox"
              checked={allChecked}
              onChange={e => handlePermissionChange(mod.id, 'all', e.target.checked)}
              disabled={!canEdit}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            />
          ) : (
            <span className="text-gray-300 text-lg select-none">—</span>
          )}
        </td>
      </tr>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">

      {/* Header */}
      <div className="bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Authorization Management</h1>
            <p className="text-gray-600 mt-1">Manage user permissions and access controls</p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <button
                onClick={() => selectedUser && loadUserPermissions(selectedUser)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            )}
            <button
              onClick={handleSavePermissions}
              disabled={saving || !canEdit || !hasChanges}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">

        {saveMessage && (
          <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
            saveMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {saveMessage.text}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mr-3" />
            <p className="text-gray-600">Fetching roles and permissions...</p>
          </div>
        ) : (
          <div className="space-y-6">

            {/* Role & User Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Role & User Selection</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Role</label>
                  <select
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Please select role</option>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedRole
                      ? `${users.length} user${users.length !== 1 ? 's' : ''} in selected role`
                      : 'Select a role to view users'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select User</label>
                  <select
                    value={selectedUser}
                    onChange={e => setSelectedUser(e.target.value)}
                    disabled={loadingUsers || !selectedRole}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingUsers    ? <option value="">Fetching users...</option>
                     : !selectedRole ? <option value="">Select a role first</option>
                     : users.length  ? <><option value="">Please select user</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</>
                                     : <option value="">No users found for this role</option>}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {loadingUsers ? 'Fetching users...'
                     : selectedUserData ? `Editing: ${selectedUserData.email}`
                     : 'Select a user to edit permissions'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h2>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => handleQuickAction('read', true)}  disabled={!canEdit} className="px-3 py-1.5 text-sm bg-blue-50   hover:bg-blue-100   text-blue-700   border border-blue-200   rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"><Eye       className="w-3.5 h-3.5" />Grant All Read</button>
                <button onClick={() => handleQuickAction('read', false)} disabled={!canEdit} className="px-3 py-1.5 text-sm bg-gray-50   hover:bg-gray-100   text-gray-600   border border-gray-200   rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"><Eye       className="w-3.5 h-3.5" />Revoke All Read</button>
                <button onClick={() => handleQuickAction('all', true)}   disabled={!canEdit} className="px-3 py-1.5 text-sm bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"><Shield    className="w-3.5 h-3.5" />Grant All Applicable</button>
                <button onClick={() => handleQuickAction('all', false)}  disabled={!canEdit} className="px-3 py-1.5 text-sm bg-red-50    hover:bg-red-100    text-red-700    border border-red-200    rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" />Revoke All</button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                READ controls sidebar visibility for all modules. CREATE / UPDATE / DELETE apply only to Field Instructions.
              </p>
            </div>

            {/* Permissions Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Permissions
                  {modules.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-400">({modules.length} modules)</span>
                  )}
                </h2>
                <div className="text-sm text-gray-500 bg-blue-50 px-3 py-1.5 rounded-lg">
                  <span className="font-medium text-blue-700">Tip:</span> Unchecking READ hides the module from the sidebar menu.
                </div>
              </div>

              {loadingPermissions ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 mr-3" />
                  <span className="text-gray-600">Fetching user permissions...</span>
                </div>
              ) : !selectedUser ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <Shield className="w-8 h-8 mr-3" />
                  <span>Select a role and user to view and edit permissions</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                          MODULE
                        </th>
                        {ALL_PERMS.map(p => (
                          <th key={p} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="flex flex-col items-center gap-0.5">
                              <span>{PERM_LABELS[p].label}</span>
                              <span className="text-xs text-gray-400 font-normal normal-case">{PERM_LABELS[p].sub}</span>
                            </div>
                          </th>
                        ))}
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex flex-col items-center gap-0.5">
                            <span>ALL</span>
                            <span className="text-xs text-gray-400 font-normal">Master toggle</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parentModules.map(mod => {
                        const children  = childrenByParent[mod.id] || [];
                        const hasChildren = children.length > 0;
                        const isExpanded  = expandedGroups.has(mod.id);

                        return (
                          <React.Fragment key={mod.id}>
                            {/* Parent row */}
                            {renderModuleRow(mod)}

                            {/* Subcategory group header (if has children) */}
                            {hasChildren && (
                              <tr className="bg-blue-50/40">
                                <td colSpan={2} className="px-6 py-2">
                                  <button
                                    onClick={() => toggleGroup(mod.id)}
                                    className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900"
                                  >
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    {mod.id === 'settings' ? 'Settings Menu' : 'Data Categories'} ({children.length})
                                  </button>
                                </td>
                                <td colSpan={4} className="px-6 py-2 text-right">
                                  {canEdit && (
                                    <div className="flex items-center justify-end gap-3">
                                      <button
                                        onClick={() => handleParentToggle(mod.id, true)}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                      >
                                        Grant All
                                      </button>
                                      <span className="text-gray-300">|</span>
                                      <button
                                        onClick={() => handleParentToggle(mod.id, false)}
                                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                                      >
                                        Revoke All
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}

                            {/* Child rows (expanded) */}
                            {hasChildren && isExpanded && children.map(child => renderModuleRow(child, true))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorizationManagement;
